import { signal } from "@preact/signals";
import { newId, type VenueId, type DeviceId } from "@orderman/shared";
import { kvGet, kvSet } from "./idb.js";

/**
 * Laufzeit-Einstellungen (Server-URL, Venue) — persistiert in localStorage, damit die
 * App auch beim Kaltstart ohne Netz weiß, wohin sie syncen soll. Der Kellner kommt aus
 * der Anmeldung (state/auth.ts), nicht mehr aus den Einstellungen.
 */

const LS = window.localStorage;

/** Fester Dev-Venue, passend zum Seed-Skript (tools/seed-menu.ts). */
export const DEV_VENUE = "11111111-1111-1111-1111-111111111111" as VenueId;

export interface Settings {
  serverUrl: string;
  venueId: VenueId;
}

// Geräte-ID stabil halten (für HLC/Sync). Primär in localStorage (synchron lesbar), zusätzlich
// in IndexedDB gespiegelt: iOS-PWAs verlieren localStorage bei Storage-Eviction, IndexedDB
// überlebt eher. hydrateIdentity() stellt die ID beim Start aus IndexedDB wieder her, BEVOR eine
// neue erzeugt wird → sonst legt jedes erneute Beitreten eine neue `geraet`-Zeile an (Wildwuchs).
let deviceId: DeviceId | null = (LS.getItem("orderman.deviceId") as DeviceId | null) || null;

// Server-URL wird aus der Herkunft abgeleitet (nicht mehr konfigurierbar):
//  - Dev: esbuild liefert die PWA auf :8000 aus → API getrennt auf :3000.
//  - Prod: hinter Caddy/HTTPS (Port 443) wird die PWA same-origin ausgeliefert → API
//    über dieselbe Origin (/api…); kein Port, kein Mixed-Content.
const defaultServer =
  location.port === "8000" ? `${location.protocol}//${location.hostname}:3000` : location.origin;

// Venue aus der URL (?venue=…) hat Vorrang — so konfiguriert ein veranstaltungs-
// spezifischer Link/QR die App direkt (wichtig, wenn von der Cloud geladen, wo es kein
// eindeutiges /api/venue gibt). Wird gecacht, damit es Reloads überlebt.
const venueParam = new URLSearchParams(location.search).get("venue");
if (venueParam) LS.setItem("orderman.venueId", venueParam);
/** Wurde die Venue explizit per URL gesetzt? Dann nicht durch /api/venue überschreiben. */
export const venuePinned = venueParam !== null;

export const settings = signal<Settings>({
  serverUrl: defaultServer,
  venueId: (venueParam as VenueId | null) ?? (LS.getItem("orderman.venueId") as VenueId | null) ?? DEV_VENUE,
});

export function getDeviceId(): DeviceId {
  if (!deviceId) {
    deviceId = newId() as DeviceId;
    LS.setItem("orderman.deviceId", deviceId);
    void kvSet("deviceId", deviceId);
  }
  return deviceId;
}

/**
 * Kurzbeschreibung dieses Geräts (Modell/Plattform) — hilft dem Betreiber, das physische Handy
 * dem Inventar-Eintrag zuzuordnen (statt nur an der UUID). UA-Client-Hints (Chromium/Android)
 * liefern das echte Modell; sonst grober Parse des User-Agent (iOS meldet nur „iPhone"). Wird
 * einmal ermittelt und im Präsenz-Ping (handy-seen.php) mitgeschickt.
 */
function browserName(ua: string): string {
  if (/Edg\//.test(ua)) return "Edge";
  if (/OPR\/|\bOpera\b/.test(ua)) return "Opera";
  if (/SamsungBrowser\//.test(ua)) return "Samsung Internet";
  if (/FxiOS\/|Firefox\//.test(ua)) return "Firefox";
  if (/CriOS\//.test(ua)) return "Chrome";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Version\/.*Safari\//.test(ua)) return "Safari";
  return "";
}
function osName(ua: string): string {
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android";
  if (/CrOS/.test(ua)) return "ChromeOS";
  if (/Windows/.test(ua)) return "Windows";
  if (/Mac OS X/.test(ua)) return "Mac";
  if (/Linux/.test(ua)) return "Linux";
  return "";
}
// Grobe Marke aus dem Modell-Code (best effort; sonst nur der Code).
function brandFromModel(m: string): string {
  if (/^SM-|Galaxy/i.test(m)) return "Samsung";
  if (/Pixel/i.test(m)) return "Google";
  if (/^(Redmi|POCO|Mi\b|M1[0-9]|M2[0-9]|2[0-9]{3}|Xiaomi)/i.test(m)) return "Xiaomi";
  if (/OnePlus/i.test(m)) return "OnePlus";
  return "";
}
/** Label bauen: „Gerät/Modell · Browser" (Handy) bzw. „OS · Browser" (Desktop). */
function buildLabel(model: string, platform: string): string {
  const ua = navigator.userAgent;
  const br = browserName(ua);
  const m = model.trim();
  let dev: string;
  if (/iPhone/.test(ua)) dev = "iPhone";
  else if (/iPad/.test(ua)) dev = "iPad";
  else if (m && m !== "K" && m !== "wv") {
    const b = brandFromModel(m);
    dev = b ? `${b} ${m}` : m;
  } else dev = platform || osName(ua) || "Gerät";
  return [dev, br].filter(Boolean).join(" · ");
}
// Fallback ohne Client Hints: Android-Modell aus dem UA (bei Chrome-UA-Reduction oft „K").
function uaAndroidModel(): string {
  return navigator.userAgent.match(/Android[^;]*;\s*([^;)]+?)(?:\s+Build|\))/)?.[1]?.trim() ?? "";
}
let _deviceLabel = buildLabel(uaAndroidModel(), "");
export function getDeviceLabel(): string {
  return _deviceLabel;
}
// Genaueres Modell via UA-Client-Hints (nur Chromium/Android) — best effort, verfeinert das Label.
const uaData = (navigator as unknown as {
  userAgentData?: { getHighEntropyValues?: (h: string[]) => Promise<Record<string, string>> };
}).userAgentData;
if (uaData?.getHighEntropyValues) {
  void uaData
    .getHighEntropyValues(["model", "platform"])
    .then((h) => {
      const l = buildLabel(h.model ?? "", h.platform ?? "");
      if (l) _deviceLabel = l;
    })
    .catch(() => {
      /* Client Hints nicht verfügbar → Fallback-Label bleibt */
    });
}

/**
 * Geräte-Schreib-Token: bei der Registrierung (enroll.php) einmalig bezogen und hier stabil
 * abgelegt. Die PWA schickt ihn beim Bestellen über die Cloud mit (events.php) — nur damit
 * gilt das Gerät als vom Betreiber freigeschaltet. Ohne Token kann über die Cloud nicht
 * geschrieben werden (Gerät muss den Enroll-QR scannen). Stabil wie die deviceId → localStorage.
 */
export function getDeviceToken(): string | null {
  return LS.getItem("orderman.deviceToken");
}
export function setDeviceToken(token: string): void {
  LS.setItem("orderman.deviceToken", token);
  void kvSet("deviceToken", token); // Spiegel für hydrateIdentity() (übersteht localStorage-Eviction)
}

/**
 * Identität (deviceId + Geräte-Token) beim Start aus IndexedDB wiederherstellen, falls
 * localStorage geleert wurde (iOS-PWA-Eviction). MUSS vor dem ersten getDeviceId()/getDeviceToken()
 * und vor der Join/Enroll-Entscheidung laufen. localStorage bleibt der synchrone Primärspeicher —
 * hier nur Restore (LS leer → aus IDB) bzw. Backup (LS gesetzt → nach IDB spiegeln).
 */
export async function hydrateIdentity(): Promise<void> {
  try {
    const lsId = LS.getItem("orderman.deviceId");
    const idbId = await kvGet<string>("deviceId");
    if (!lsId && idbId) {
      LS.setItem("orderman.deviceId", idbId);
      deviceId = idbId as DeviceId;
    } else if (lsId && idbId !== lsId) {
      await kvSet("deviceId", lsId);
    }
    const lsTok = LS.getItem("orderman.deviceToken");
    const idbTok = await kvGet<string>("deviceToken");
    if (!lsTok && idbTok) {
      LS.setItem("orderman.deviceToken", idbTok);
    } else if (lsTok && idbTok !== lsTok) {
      await kvSet("deviceToken", lsTok);
    }
  } catch {
    /* IndexedDB nicht verfügbar → localStorage gilt */
  }
}

/**
 * Gerätename aus dem Inventar (bezeichnung) — beim Beitritt gesetzt/gemerkt und regelmäßig aus
 * assignment.php aufgefrischt. Wird im Login angezeigt, damit man das physische Handy beim
 * Zuordnen im Portal wiedererkennt (v. a. Betreiber-Geräte, die ohne Venue reinkommen).
 */
export const deviceName = signal<string>(LS.getItem("orderman.deviceName") ?? "");
export function setDeviceName(name: string): void {
  deviceName.value = name;
  if (name) LS.setItem("orderman.deviceName", name);
  else LS.removeItem("orderman.deviceName");
}

/**
 * Geräte-Herkunft ('betreiber' | 'byd' | ''), beim Beitritt gesetzt + aus assignment.php
 * aufgefrischt. Ein Betreiber-Gerät wird über das Inventar/Zuweisung verwaltet — es bietet daher
 * KEIN „Gerät neu einrichten" per Code an.
 */
/** Gerät ist registriert, aber (noch) keiner Veranstaltung zugewiesen → sauberer Login, KEIN
 *  Venue-Sync (sonst lädt der Tick Personal/Karte der alten Venue nach). Hier (config = Blatt-
 *  Modul), damit store.ts UND assignment.ts es ohne Import-Zyklus lesen können. */
export const deviceUnassigned = signal(false);

export const deviceOwnerClass = signal<string>(LS.getItem("orderman.deviceOwnerClass") ?? "");
export function setDeviceOwnerClass(oc: string | null | undefined): void {
  const v = oc ?? "";
  deviceOwnerClass.value = v;
  if (v) LS.setItem("orderman.deviceOwnerClass", v);
  else LS.removeItem("orderman.deviceOwnerClass");
}

/**
 * Reset-Generation der Venue (Betriebsdaten-Generation, s. „Veranstaltung zurücksetzen"). Pro
 * Venue in localStorage gehalten; bei jedem Push mitgeschickt. Meldet der Server eine höhere,
 * leert die PWA ihren Bestell-Cache und übernimmt die neue Generation.
 */
export function getResetGen(venueId: string): number {
  return Number(LS.getItem(`orderman.resetGen:${venueId}`) ?? 0) || 0;
}
export function setResetGen(venueId: string, gen: number): void {
  LS.setItem(`orderman.resetGen:${venueId}`, String(gen));
}
/** Wurde für diese Venue je eine reset-Generation gelernt? (Key vorhanden ≠ „behind", sondern „nie gekannt".) */
export function hasResetGen(venueId: string): boolean {
  return LS.getItem(`orderman.resetGen:${venueId}`) !== null;
}

export function updateSettings(patch: Partial<Settings>): void {
  settings.value = { ...settings.value, ...patch };
  // Nur die Venue cachen (für Offline-Kaltstart); die Server-URL ist abgeleitet.
  LS.setItem("orderman.venueId", settings.value.venueId);
}

// Anzeige-Vorlieben pro Gerät (Sehkraft/Display-Größe variieren je Kellner). Persistiert,
// damit jedes Handy seine Einstellung über Reloads behält.
export interface DisplayPrefs {
  cols: number; // Spalten im Artikel-Raster
  fontScale: number; // Multiplikator für Artikel-Schrift (1.0 = Standard)
}

const DISPLAY_DEFAULTS: DisplayPrefs = { cols: 3, fontScale: 1.0 };

// Deckel bei 4 Spalten (mehr wird auf Handys unbrauchbar) — clampt auch einen früher
// persistierten Wert (z. B. 5), der sonst gespeichert überleben würde.
const clampCols = (n: number) => Math.min(4, Math.max(2, Math.round(n)));

function loadDisplayPrefs(): DisplayPrefs {
  const raw = LS.getItem("orderman.displayPrefs");
  if (!raw) return DISPLAY_DEFAULTS;
  try {
    const p = JSON.parse(raw) as Partial<DisplayPrefs>;
    return {
      cols: typeof p.cols === "number" ? clampCols(p.cols) : DISPLAY_DEFAULTS.cols,
      fontScale: typeof p.fontScale === "number" ? p.fontScale : DISPLAY_DEFAULTS.fontScale,
    };
  } catch {
    return DISPLAY_DEFAULTS;
  }
}

export const displayPrefs = signal<DisplayPrefs>(loadDisplayPrefs());

export function updateDisplayPrefs(patch: Partial<DisplayPrefs>): void {
  displayPrefs.value = { ...displayPrefs.value, ...patch };
  LS.setItem("orderman.displayPrefs", JSON.stringify(displayPrefs.value));
}
